
import React, { useState } from 'react';
import { UserRole } from '../types.ts';
import Logo from './Logo.tsx';

interface DashboardLayoutProps {
  role: UserRole;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onLogout: () => void;
  hasUnread: boolean;
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ 
  role, 
  activeTab, 
  setActiveTab, 
  onLogout, 
  hasUnread, 
  children 
}) => {
  const isProfessional = role === UserRole.PROFESSIONAL;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const primaryMobileTabs = isProfessional 
    ? [
        { id: 'requests', label: 'Requests', icon: 'fa-inbox' },
        { id: 'chats', label: 'Messages', icon: 'fa-comments', badge: hasUnread },
        { id: 'reciprocity', label: 'Insights', icon: 'fa-exchange-alt' },
      ]
    : [
        { id: 'connect', label: 'Connect', icon: 'fa-users' },
        { id: 'chats', label: 'Messages', icon: 'fa-comments', badge: hasUnread },
        { id: 'strategist', label: 'Plan', icon: 'fa-chess-knight' },
      ];

  const secondaryTabs = isProfessional
    ? [
        { id: 'reciprocity', label: 'Student Insights', icon: 'fa-exchange-alt' },
        { id: 'rewards', label: 'Earn Rewards', icon: 'fa-award' },
        { id: 'calendar', label: 'My Schedule', icon: 'fa-calendar' },
        { id: 'strategist', label: 'Career Planner', icon: 'fa-chess-knight' },
        { id: 'profile', label: 'My Profile', icon: 'fa-user-circle' },
        { id: 'help', label: 'Help & Support', icon: 'fa-question-circle' },
      ]
    : [
        { id: 'prep', label: 'Meeting Prep', icon: 'fa-clipboard-check' },
        { id: 'automation', label: 'Auto Follow-up', icon: 'fa-sync-alt' },
        { id: 'events', label: 'Networking Events', icon: 'fa-calendar-alt' },
        { id: 'jobs', label: 'Job Board', icon: 'fa-briefcase' },
        { id: 'membership', label: 'Upgrade Plan', icon: 'fa-credit-card' },
        { id: 'profile', label: 'My Profile', icon: 'fa-user-circle' },
        { id: 'help', label: 'Help & Support', icon: 'fa-question-circle' },
      ];

  const handleTabSwitch = (id: string) => {
    setActiveTab(id);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 overflow-x-hidden transition-colors duration-500">
      {/* Desktop Sidebar - Updated for Distinct Light/Dark Modes */}
      <aside className="w-72 bg-white border-r border-slate-200 dark:bg-blue-950 dark:border-none p-8 flex flex-col fixed h-full z-50 shadow-2xl shrink-0 hidden md:flex transition-colors duration-500">
        <div className="mb-12">
           {/* Auto-switching Logo variant based on theme context (handled by CSS via parent class usually, but explicit variant passed here) */}
           <div className="dark:hidden">
              <Logo variant="dark" onClick={() => setActiveTab(isProfessional ? 'requests' : 'connect')} />
           </div>
           <div className="hidden dark:block">
              <Logo variant="light" onClick={() => setActiveTab(isProfessional ? 'requests' : 'connect')} />
           </div>
        </div>
        
        <nav className="flex-1 space-y-3 overflow-y-auto scrollbar-hide">
          {!isProfessional ? (
            <>
              <button onClick={() => setActiveTab('connect')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'connect' || activeTab === 'professionals' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-users ${activeTab === 'connect' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Connect
              </button>
              <button onClick={() => setActiveTab('chats')} className={`relative w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'chats' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-comments ${activeTab === 'chats' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Messages
                {hasUnread && <span className="absolute right-6 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
              </button>
              <button onClick={() => setActiveTab('prep')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'prep' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-clipboard-check ${activeTab === 'prep' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Meeting Prep
              </button>
              <button onClick={() => setActiveTab('automation')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'automation' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-sync-alt ${activeTab === 'automation' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Auto Follow-up
              </button>
              <button onClick={() => setActiveTab('strategist')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'strategist' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-chess-knight ${activeTab === 'strategist' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Career Planner
              </button>
              <button onClick={() => setActiveTab('events')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'events' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-calendar-alt ${activeTab === 'events' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Events
              </button>
              <button onClick={() => setActiveTab('jobs')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'jobs' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-briefcase ${activeTab === 'jobs' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Job Board
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setActiveTab('requests')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'requests' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-inbox ${activeTab === 'requests' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Inbound Requests
              </button>
              <button onClick={() => setActiveTab('chats')} className={`relative w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'chats' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-comments ${activeTab === 'chats' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Messages
                {hasUnread && <span className="absolute right-6 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
              </button>
              <button onClick={() => setActiveTab('reciprocity')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'reciprocity' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-exchange-alt ${activeTab === 'reciprocity' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Student Insights
              </button>
              <button onClick={() => setActiveTab('calendar')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'calendar' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-calendar ${activeTab === 'calendar' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> My Schedule
              </button>
              <button onClick={() => setActiveTab('rewards')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'rewards' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-award ${activeTab === 'rewards' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Earn Rewards
              </button>
              <button onClick={() => setActiveTab('strategist')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'strategist' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-chess-knight ${activeTab === 'strategist' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Career Planner
              </button>
            </>
          )}

          <div className="pt-8 border-t border-slate-100 dark:border-white/5 space-y-2">
            {!isProfessional && (
              <button onClick={() => setActiveTab('membership')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'membership' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
                <i className={`fas fa-credit-card ${activeTab === 'membership' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Upgrade Plan
              </button>
            )}
            <button onClick={() => setActiveTab('profile')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'profile' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
              <i className={`fas fa-user-circle ${activeTab === 'profile' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> My Profile
            </button>
            <button onClick={() => setActiveTab('help')} className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'help' ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-slate-400 hover:text-blue-900 dark:text-white/40 dark:hover:text-white'}`}>
              <i className={`fas fa-question-circle ${activeTab === 'help' ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}></i> Help & Support
            </button>
          </div>
        </nav>
        
        <div className="mt-auto border-t border-slate-100 dark:border-white/10 pt-6 flex flex-col gap-2">
           <button onClick={onLogout} className="w-full py-4 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition font-black uppercase text-[10px] tracking-widest text-left px-6">
             Logout
           </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-100 dark:border-slate-800 flex items-center justify-around px-2 z-[100] md:hidden shadow-4xl">
        {primaryMobileTabs.map(tab => (
          <button 
            key={tab.id}
            onClick={() => handleTabSwitch(tab.id)}
            className={`flex flex-col items-center justify-center w-full h-full transition-all relative ${activeTab === tab.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}
          >
            <i className={`fas ${tab.icon} text-lg mb-1`}></i>
            <span className="text-[8px] font-black uppercase tracking-widest">{tab.label}</span>
            {tab.badge && <span className="absolute top-4 right-1/3 w-1.5 h-1.5 bg-blue-500 rounded-full"></span>}
            {activeTab === tab.id && <span className="absolute bottom-1 w-5 h-1 bg-indigo-600 dark:bg-indigo-400 rounded-full"></span>}
          </button>
        ))}
        <button 
          onClick={() => setIsMobileMenuOpen(true)}
          className="flex flex-col items-center justify-center w-full h-full text-slate-400 dark:text-slate-500"
        >
          <i className="fas fa-th-large text-lg mb-1"></i>
          <span className="text-[8px] font-black uppercase tracking-widest">More</span>
        </button>
      </nav>

      {/* Mobile More Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-blue-950/95 dark:bg-black/95 backdrop-blur-2xl z-[200] flex flex-col p-8 md:hidden animate-in fade-in duration-300">
           <header className="flex justify-between items-center mb-10">
              <Logo variant="light" />
              <button onClick={() => setIsMobileMenuOpen(false)} className="w-12 h-12 rounded-2xl bg-white/10 text-white flex items-center justify-center text-xl border border-white/10 active:scale-95 transition-transform">
                <i className="fas fa-times"></i>
              </button>
           </header>
           
           <div className="flex-1 overflow-y-auto scrollbar-hide pb-12">
              <div className="grid grid-cols-2 gap-3">
                 {secondaryTabs.map(tab => (
                    <button 
                      key={tab.id}
                      onClick={() => handleTabSwitch(tab.id)}
                      className={`flex flex-col items-center justify-center p-6 rounded-3xl border transition-all ${
                        activeTab === tab.id 
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow-2xl' 
                        : 'bg-white/5 border-white/5 text-slate-300 hover:bg-white/10'
                      }`}
                    >
                      <i className={`fas ${tab.icon} text-xl mb-3 ${activeTab === tab.id ? 'text-white' : 'text-indigo-400'}`}></i>
                      <span className="text-[9px] font-black uppercase tracking-widest text-center">{tab.label}</span>
                    </button>
                 ))}
              </div>
           </div>

           <div className="mt-auto pt-6 border-t border-white/5 flex flex-col gap-3">
              <button onClick={onLogout} className="w-full py-4 bg-red-500/10 text-red-400 rounded-2xl font-black uppercase text-[10px] tracking-widest border border-red-500/20 active:scale-95 transition-transform">
                Secure Logout
              </button>
           </div>
        </div>
      )}

      {/* Content Area */}
      <main className="flex-1 md:ml-72 pb-24 md:pb-0 min-h-screen overflow-x-hidden">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
